import { useNavigate } from "@tanstack/react-router";
import { surfaceBase } from "@web/components/ui";
import { useEffect, useState } from "react";
import { SelectField } from "@/components/ui";
import { useToast } from "@/contexts";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import { createGroup, listOrgGroups } from "@/features/organizations/groups";
import type { Group, UserProfile } from "@/types";

export default function SettingsGroupsNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [type, setType] = useState("office");
  const [parentId, setParentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error } = await getCurrentUserProfile();
      if (error) {
        toast.showAlert("Error", error.message, "error");
        setLoading(false);
        return;
      }
      if (!data?.orgId) {
        setLoading(false);
        return;
      }
      setProfile(data);
      const result = await listOrgGroups(data.orgId);
      if (result.data) {
        setGroups(result.data);
      }
      setLoading(false);
    })();
  }, [toast]);

  function handleNameChange(value: string) {
    setName(value);
    if (!slugEdited) {
      setSlug(
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      );
    }
  }

  function handleSlugChange(value: string) {
    setSlugEdited(true);
    setSlug(value);
  }

  async function handleCreate() {
    if (!name.trim()) {
      toast.showAlert("Name required", "Group name is required.", "error");
      return;
    }
    if (!slug.trim()) {
      toast.showAlert("Slug required", "Group slug is required.", "error");
      return;
    }
    if (!profile?.orgId) return;

    setSaving(true);
    const { error } = await createGroup(profile.orgId, {
      name: name.trim(),
      slug: slug.trim(),
      type,
      parentId: parentId || undefined,
    });
    setSaving(false);

    if (error) {
      toast.showAlert("Create failed", error.message, "error");
    } else {
      toast.showAlert("Created", "Group created.", "success");
      navigate({ to: "/settings/groups" });
    }
  }

  const availableParents = groups.filter((g) => !g.parentId);

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="h-screen overflow-y-auto">
          <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
              Loading...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!profile?.orgId) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="h-screen overflow-y-auto">
          <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
            <span className="text-text-dim text-[1rem]">You are not part of an organization.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <div className="h-screen overflow-y-auto">
        <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate({ to: "/settings/groups" })}
              className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] cursor-pointer"
            >
              <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
            </button>
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
              Create group
            </span>
          </div>

          <div className={`${surfaceBase} bg-card p-5 flex flex-col gap-4`}>
            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="group-name"
                className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
              >
                Name
              </label>
              <input
                id="group-name"
                type="text"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Engineering"
                className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="group-slug"
                className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
              >
                Slug
              </label>
              <input
                id="group-slug"
                type="text"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="engineering"
                className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
              />
            </div>

            <SelectField
              label="Type"
              value={type}
              options={[
                { label: "Office", value: "office" },
                { label: "Department", value: "department" },
                { label: "Custom", value: "custom" },
              ]}
              onValueChange={(v) => setType(v)}
            />

            <SelectField
              label="Parent group (optional)"
              value={parentId}
              options={[
                { label: "None", value: "" },
                ...availableParents.map((g) => ({ label: g.name, value: g.id })),
              ]}
              onValueChange={(v) => setParentId(v)}
            />

            <button
              type="button"
              onClick={handleCreate}
              disabled={saving}
              className="bg-aqua rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer disabled:opacity-50"
            >
              {saving ? "Creating..." : "Create group"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
